// Учётные записи площадки «Авито». Одна строка — одна учётная запись (правило 2): как мы её
// зовём между собой, что ею уже можно делать и в каком состоянии её ключ доступа.
//
// Главное, что строка обязана донести: **наборов доступов на записи ДВА, и они не
// взаимозаменяемы** (тз 6.1). Переписка работает только ключом компании, вакансии ведутся
// ключом сотрудника — пакет размещения привязан к сотруднику. Поэтому в подписи стоит не
// «доступы есть», а перечень того, что уже работает: увидев «только вакансии», человек не
// пойдёт искать, почему молчит помощник.
//
// Отборов у представления нет вовсе (`route.ts`, FILTERS_BY_VIEW.accounts): записей у
// заказчика три, отбирать среди трёх нечего, а пустая полоса фильтров съедала бы строку
// списка ни за чем. Из этого следует и упрощение пустого состояния: пусто здесь может
// означать ровно одно — записей нет, — и разбирать случай «пусто из-за отбора» не нужно.

import { useState } from 'react';
import { RiAddLine, RiPlugLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';

import type { AccountOut } from '../api/platform';
import { GateNote, useCan } from '../components/gates';
import { ACCOUNT_FORMS } from '../dictionaries';
import { useAccountsQuery } from '../queries';
import type { HiringRoute } from '../route';
import { BLANK, PANEL_NEW_ACCOUNT, hiringHref, openPanelHref } from '../route';

/** Подключение записей и заведение доступов поручено администратору (матрица тз, раздел 3.2). */
export const MANAGE_PERMISSION = 'hiring.avito.manage_accounts';

/** Кому идти за правом. Стоит рядом с каждым отказом: отказ без адреса — это молчание. */
export const MANAGE_OWNER =
  'Учётные записи площадки подключает администратор системы — он же кладёт в них доступы.';

/**
 * Состояние ключа доступа по всей записи.
 *
 * Четыре, а не три, и четвёртое здесь не педантизм (правило 7). Набор доступов и ключ — разные
 * вещи: заведение доступов ключа НЕ выдаёт, он выпускается сам при первом обращении к площадке
 * и живёт сутки. То есть сразу после «Заменить доступы» запись честно стоит без ключа, и
 * написать ей «ключ истёк» значило бы соврать про поломку там, где всё в порядке.
 */
export type СостояниеКлюча = 'доступов нет' | 'ключ не выдан' | 'ключ истёк' | 'ключ действует';

export function состояниеКлюча(account: AccountOut): СостояниеКлюча {
  const заведённые = account.credentials.filter((набор) => набор.configured);
  if (заведённые.length === 0) {
    return 'доступов нет';
  }
  // Худшее из двух побеждает: если один ключ протух, запись работает наполовину, и увидеть
  // это надо в списке, а не в карточке после жалобы «помощник не отвечает».
  if (заведённые.some((набор) => набор.token_valid_until != null && !набор.token_alive)) {
    return 'ключ истёк';
  }
  if (заведённые.some((набор) => набор.token_valid_until == null)) {
    return 'ключ не выдан';
  }
  return 'ключ действует';
}

const ЦВЕТ_КЛЮЧА: Readonly<Record<СостояниеКлюча, 'gray' | 'blue' | 'orange' | 'green'>> = {
  // Цвет ДУБЛИРУЕТ слово, а не заменяет его: метка читается и в чёрно-белой распечатке.
  'доступов нет': 'gray',
  'ключ не выдан': 'blue',
  'ключ истёк': 'orange',
  'ключ действует': 'green',
};

/**
 * Что этой записью уже можно делать. Не «ключ есть», а перечень работающего: человек читает
 * строку ради ответа на вопрос «почему не идёт переписка», а не ради инвентаризации ключей.
 *
 * `can_message` без `can_publish` не бывает: переписка требует ключа компании, а он же
 * закрывает и признак «хоть один набор заведён».
 */
function чтоУмеет(account: AccountOut): string | null {
  if (account.can_message && account.can_publish) {
    return 'переписка и вакансии';
  }
  if (account.can_publish) {
    return 'только вакансии: ключа компании нет, переписка не пойдёт';
  }
  return null;
}

export function AccountsList({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { search, rowId } = route;
  const canManage = useCan(MANAGE_PERMISSION);
  const query = useAccountsQuery();

  // Отказ по нажатию, а не выключенная кнопка. Выключенная не доводит нажатие до обработчика:
  // человек жмёт и не получает ничего, и это неотличимо от сломанной программы (правило 10).
  const [отказ, setОтказ] = useState<string | null>(null);

  function подключить() {
    if (!canManage) {
      setОтказ(`Права подключать учётные записи площадки у вас нет. ${MANAGE_OWNER}`);
      return;
    }
    setОтказ(null);
    navigate(openPanelHref(route, PANEL_NEW_ACCOUNT, BLANK));
  }

  const кнопка = (
    <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={подключить}>
      <Button.Icon as={RiAddLine} />
      Подключить учётную запись
    </Button.Root>
  );

  return (
    <ListPane
      caption={
        query.data === undefined
          ? 'Учётные записи площадки'
          : countLabel(query.data.total, ACCOUNT_FORMS)
      }
      sliceAt={parseMoment(query.data?.generated_at)}
      width='имена'
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='учётные записи площадки «Авито»'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiPlugLine}
            title='Учётные записи площадки ещё не подключены'
            description={`Пока нет ни одной записи, площадка молчит целиком: вакансию не разместить, отклик не принять, переписку с соискателем не вести. Запись заводится в два шага — сначала сама запись, потом два набора доступов к ней. ${MANAGE_OWNER}`}
            action={
              <div className='flex flex-col items-center gap-2'>
                {кнопка}
                {отказ !== null && <GateNote>{отказ}</GateNote>}
              </div>
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((account) => {
              const умеет = чтоУмеет(account);
              const ключ = состояниеКлюча(account);
              return (
                <ListRow
                  key={account.id}
                  to={hiringHref('accounts', search, account.id)}
                  selected={account.id === rowId}
                  title={account.title}
                  // Когда запись не умеет ничего, метка справа уже сказала «доступов нет», и
                  // повторять это подписью нельзя: одно и то же двумя способами в одной строке
                  // человек читает как два разных сообщения и ищет между ними разницу. Вместо
                  // повтора в подписи стоит примечание — чья это запись, — то есть настоящий
                  // второй факт, который и различает три одинаковые с виду строки.
                  subtitle={
                    умеет ?? account.note ?? `подключена ${formatDateOnly(account.created_at)}`
                  }
                  badge={
                    <Badge.Root variant='lighter' color={ЦВЕТ_КЛЮЧА[ключ]} size='medium'>
                      {ключ}
                    </Badge.Root>
                  }
                />
              );
            })}

            <div className='flex flex-col items-center gap-2 p-3'>
              {кнопка}
              {отказ !== null && <GateNote>{отказ}</GateNote>}
            </div>
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}
